import { useQuery } from '@tanstack/react-query';
import { API } from '../api';
import { useAuthState } from '../state/useAuthState';
import type { ReleaseFlag } from '../shared/types';
import { ReleaseFlagKey } from '../constants';
import { persistedQueryClient } from '../components/CacheableQueryClientProvider';

type ReleaseFlagsMap = Record<string, boolean>;

const fetchReleaseFlags = async (
  organizationId?: number,
): Promise<ReleaseFlag[]> => {
  const orgParams = organizationId ? { orgId: organizationId } : {};
  const url = 'release_flags/';
  const response = await API.get(url, { params: orgParams });

  // An empty/short-circuited 2xx body results in an empty string response from axios. Throw an error so
  // useQuery treats it as a failed fetch so it'll retry next time (as opposed to caching an invalid value)
  if (!Array.isArray(response.data)) {
    throw new Error('Unexpected non-array release_flags response');
  }
  return response.data;
};

/**
 * Create a map of release flags for easy lookup
 */
const createReleaseFlagsMap = (
  releaseFlags: ReleaseFlag[],
): ReleaseFlagsMap => {
  return releaseFlags.reduce((acc, flag) => {
    acc[flag.flagKey] = flag.flagEnabled;
    return acc;
  }, {} as ReleaseFlagsMap);
};

/** Shared query for release flags, used as the base for all release flag hooks. */
const useReleaseFlagsQuery = (): ReturnType<typeof useQuery<ReleaseFlag[]>> => {
  const { user } = useAuthState();

  // Get organization ID if user has one (always 0 or 1 organization)
  const organizationId = user?.organizations?.[0]?.id;

  return useQuery(
    {
      queryKey: ['releaseFlags', organizationId],
      queryFn: () => fetchReleaseFlags(organizationId),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false,
      retry: 3,
    },
    persistedQueryClient,
  );
};

/**
 * Hook to check if a specific release flag is enabled
 *
 * If the user belongs to an organization, includes ?org_id parameter
 *
 * @param flagKey - The key of the release flag to check
 * @returns boolean indicating if the flag is enabled
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const isWaterLevelEnabled = useReleaseFlag('river_gauges');
 *
 *   if (isWaterLevelEnabled) {
 *     return <WaterLevelComponent />;
 *   }
 * }
 * ```
 */
export const useReleaseFlag = (flagKey: ReleaseFlagKey): boolean => {
  const { data: releaseFlags = [], isLoading } = useReleaseFlagsQuery();
  if (isLoading) return false;

  const releaseFlagsMap = createReleaseFlagsMap(releaseFlags);
  return releaseFlagsMap[flagKey] === true;
};

/**
 * Hook to check if release flags are still being fetched.
 * Useful for deferring UI that depends on flag-gated routes.
 */
export const useReleaseFlagsLoading = (): boolean => {
  const { isLoading } = useReleaseFlagsQuery();
  return isLoading;
};
